import React, { useState, useMemo, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sale } from '@/types/models';
import {
  correctSaleInvoiceRpc,
  correctSalePaymentEntryRpc,
  grantPostSaleDiscountRefundRpc,
  fetchSalePayments,
  fetchCustomers,
  previewLegacySaleCorrectionRpc,
  applyLegacySaleCorrectionRpc,
} from '@/services/sales';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { useAuth } from '@/context/AuthContext';
import {
  AlertTriangle,
  X,
  Edit3,
  Loader2,
  Wrench,
  User,
  UserX,
  DollarSign,
  CheckCircle,
  HelpCircle,
} from 'lucide-react';

interface SaleCorrectionModalProps {
  sale: Sale;
  isOpen: boolean;
  onClose: () => void;
}

type ItemCorrection = {
  sale_item_id: string;
  unit_price: number;
  line_discount: number;
};

export const SaleCorrectionModal: React.FC<SaleCorrectionModalProps> = ({
  sale,
  isOpen,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const { role } = useAuth();
  const isOwner = role === 'owner';

  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState(sale.notes ?? '');
  const [overallDiscount, setOverallDiscount] = useState(sale.discount ?? 0);
  const [tax, setTax] = useState(sale.tax ?? 0);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Customer modification state
  const [customerMode, setCustomerMode] = useState<'keep' | 'walk_in' | 'change'>('keep');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(sale.customer_id ?? null);
  const [customerSearch, setCustomerSearch] = useState('');

  // Payment correction state
  const [editingPayment, setEditingPayment] = useState<{
    id: string;
    amount: number;
    payment_method: string;
    reference: string | null;
    notes: string | null;
  } | null>(null);
  const [paymentNewAmount, setPaymentNewAmount] = useState<number>(0);
  const [paymentReason, setPaymentReason] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<string>('cash');
  const [paymentReference, setPaymentReference] = useState<string>('');
  const [paymentNotes, setPaymentNotes] = useState<string>('');
  const [paymentCorrectionError, setPaymentCorrectionError] = useState<string | null>(null);
  const [paymentCorrectionSuccess, setPaymentCorrectionSuccess] = useState<string | null>(null);

  // Post-sale discount refund state
  const [discountRefundMethod, setDiscountRefundMethod] = useState<'cash' | 'bank_transfer' | 'card' | 'mobile_wallet'>('cash');
  const [discountRefundRef, setDiscountRefundRef] = useState('');
  const [discountRefundSuccess, setDiscountRefundSuccess] = useState<string | null>(null);
  const [discountRefundError, setDiscountRefundError] = useState<string | null>(null);

  // Legacy COGS correction state (per laptop sale item)
  const [legacySaleItemId, setLegacySaleItemId] = useState<string | null>(null);
  const [legacyReason, setLegacyReason] = useState('');
  const [legacyFinalRam, setLegacyFinalRam] = useState('');
  const [legacyFinalStorage, setLegacyFinalStorage] = useState('');
  const [correctedUnitCost, setCorrectedUnitCost] = useState<number>(0);
  const [correctedConfigAdj, setCorrectedConfigAdj] = useState<number>(0);
  const [evidenceVerified, setEvidenceVerified] = useState(false);
  const [legacyError, setLegacyError] = useState<string | null>(null);

  const { data: legacyPreview, isLoading: isLegacyPreviewLoading } = useQuery({
    queryKey: ['sales', 'legacy-preview', sale.id],
    queryFn: () => previewLegacySaleCorrectionRpc(sale.id),
    enabled: isOpen && isOwner,
  });

  const selectedPreviewItem = useMemo(() => {
    if (!legacyPreview?.items || !legacySaleItemId) return null;
    return legacyPreview.items.find((it) => it.sale_item_id === legacySaleItemId) || null;
  }, [legacyPreview, legacySaleItemId]);

  useEffect(() => {
    if (selectedPreviewItem) {
      setCorrectedUnitCost(selectedPreviewItem.proposed_cogs ?? selectedPreviewItem.unit_cost ?? 0);
      setCorrectedConfigAdj(selectedPreviewItem.configuration_cost_adjustment ?? 0);
      setLegacyFinalRam(selectedPreviewItem.ram_snapshot || selectedPreviewItem.laptop_hw_ram || '');
      setLegacyFinalStorage(selectedPreviewItem.storage_snapshot || selectedPreviewItem.laptop_hw_storage || '');
      setEvidenceVerified(false); // Reset verification when switching items
    }
  }, [selectedPreviewItem]);

  const { data: payments = [], refetch: refetchPayments } = useQuery({
    queryKey: queryKeys.sales.payments(sale.id),
    queryFn: () => fetchSalePayments(sale.id),
    enabled: isOpen,
  });

  const { data: customers = [] } = useQuery({
    queryKey: ['customers', 'lookup', customerSearch],
    queryFn: () => fetchCustomers(customerSearch),
    enabled: isOpen && isOwner && customerMode === 'change',
  });

  const { data: completedReturns = [] } = useQuery({
    queryKey: ['sales', 'returns', sale.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('returns')
        .select('refund_amount')
        .eq('sale_id', sale.id)
        .eq('status', 'completed');
      return (data ?? []) as Array<{ refund_amount: number | null }>;
    },
    enabled: isOpen,
  });

  const [itemCorrections, setItemCorrections] = useState<Record<string, ItemCorrection>>(
    () =>
      Object.fromEntries(
        (sale.items ?? []).map((item) => [
          item.id,
          {
            sale_item_id: item.id,
            unit_price: item.unit_price,
            line_discount: item.line_discount ?? 0,
          },
        ])
      )
  );

  const totalPaymentsIn = payments.filter((p) => p.direction !== 'out').reduce((acc, p) => acc + (p.amount || 0), 0);
  const totalPaymentsOut = payments.filter((p) => p.direction === 'out').reduce((acc, p) => acc + (p.amount || 0), 0);
  const totalRefundsOut = completedReturns.reduce((acc, r) => acc + (r.refund_amount || 0), 0);
  const netCustomerReceipts = Math.max(
    0,
    (payments.length > 0 ? totalPaymentsIn : (sale.amount_paid ?? 0)) - Math.max(totalPaymentsOut, totalRefundsOut)
  );

  const proposedSubtotal = useMemo(() => {
    return (sale.items ?? []).reduce((acc, item) => {
      const corr = itemCorrections[item.id];
      if (!corr) return acc + (item.line_total ?? 0);
      const lineTotal = corr.unit_price * (item.quantity ?? 1) - corr.line_discount;
      return acc + Math.max(0, lineTotal);
    }, 0);
  }, [sale.items, itemCorrections]);

  const proposedTotal = Math.max(0, proposedSubtotal - overallDiscount + tax);
  const belowReceipts = proposedTotal < netCustomerReceipts;

  const updateItem = (id: string, field: 'unit_price' | 'line_discount', val: number) => {
    setItemCorrections((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: Math.max(0, val) },
    }));
  };

  const hasCompletedReturns =
    completedReturns.length > 0 ||
    sale.status === 'partially_refunded' ||
    sale.status === 'refunded';

  // Eligible laptop items for legacy configuration correction (owner only)
  const eligibleLaptopItems = isOwner
    ? (sale.items ?? []).filter(
        (item) => item.item_type === 'laptop' && item.laptop_id
      )
    : [];

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      if (!reason.trim()) throw new Error('A correction reason is required.');
      if (customerMode === 'change' && !selectedCustomerId) {
        throw new Error('Please select a replacement customer or switch to Walk-in.');
      }

      const payload: any = {
        sale_id: sale.id,
        reason: reason.trim(),
        notes: notes.trim() || null,
        change_to_walk_in: customerMode === 'walk_in',
      };

      if (customerMode === 'change') {
        payload.customer_id = selectedCustomerId;
      }

      if (hasCompletedReturns) {
        payload.discount = sale.discount;
        payload.tax = sale.tax;
      } else {
        payload.discount = overallDiscount;
        payload.tax = tax;
        payload.item_corrections = Object.values(itemCorrections).map((c) => ({
          sale_item_id: c.sale_item_id,
          unit_price: c.unit_price,
          line_discount: c.line_discount,
        }));
      }

      return correctSaleInvoiceRpc(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.sales.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.sales.detail(sale.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.reports.all });
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      onClose();
    },
    onError: (err: unknown) => {
      setSubmitError(parseDatabaseError(err));
    },
  });

  const { mutate: mutatePaymentCorrection, isPending: isPaymentPending } = useMutation({
    mutationFn: () => {
      if (!editingPayment) throw new Error('No payment selected for correction.');
      if (!paymentReason.trim()) throw new Error('A reason is required to correct a payment ledger entry.');
      if (paymentNewAmount <= 0) throw new Error('Payment amount must be strictly greater than zero.');

      return correctSalePaymentEntryRpc({
        payment_id: editingPayment.id,
        new_amount: paymentNewAmount,
        reason: paymentReason.trim(),
        new_payment_method: paymentMethod || null,
        new_reference: paymentReference.trim() || null,
        new_notes: paymentNotes.trim() || null,
      });
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.sales.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.sales.detail(sale.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.sales.payments(sale.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.reports.all });
      void refetchPayments();
      setPaymentCorrectionSuccess(`Payment ledger updated from Rs. ${data.old_amount.toLocaleString('en-PK')} to Rs. ${data.new_amount.toLocaleString('en-PK')}.`);
      setEditingPayment(null);
      setPaymentReason('');
      setPaymentCorrectionError(null);
    },
    onError: (err: unknown) => {
      setPaymentCorrectionError(parseDatabaseError(err));
    },
  });

  const { mutate: mutateDiscountRefund, isPending: isDiscountRefundPending } = useMutation({
    mutationFn: () => {
      if (!reason.trim()) throw new Error('A correction reason is required to grant a post-sale discount & refund.');
      const excess = netCustomerReceipts - proposedTotal;
      if (excess <= 0) throw new Error('No excess refund is due for this configuration.');
      return grantPostSaleDiscountRefundRpc({
        sale_id: sale.id,
        new_discount: overallDiscount,
        refund_amount: excess,
        refund_method: discountRefundMethod,
        reason: reason.trim(),
        reference: discountRefundRef.trim() || null,
        notes: notes.trim() || null,
      });
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.sales.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.sales.detail(sale.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.sales.payments(sale.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.reports.all });
      void refetchPayments();
      setDiscountRefundSuccess(
        `Post-sale discount granted! New invoice total: Rs. ${data.new_total.toLocaleString('en-PK')}. Refund payment of Rs. ${data.refund_amount.toLocaleString('en-PK')} recorded.`
      );
      setDiscountRefundError(null);
      setTimeout(() => {
        onClose();
      }, 1200);
    },
    onError: (err: unknown) => {
      setDiscountRefundError(parseDatabaseError(err));
    },
  });

  const { mutate: mutateLegacy, isPending: isLegacyPending } = useMutation({
    mutationFn: () => {
      if (!legacySaleItemId || !selectedPreviewItem) throw new Error('No laptop sale item selected.');
      if (!legacyReason.trim()) throw new Error('A legacy correction reason is required.');
      if (correctedUnitCost < 0) throw new Error('Unit cost cannot be negative.');

      return applyLegacySaleCorrectionRpc({
        sale_id: sale.id,
        sale_item_id: legacySaleItemId,
        reason: legacyReason.trim(),
        expected_unit_cost: selectedPreviewItem.unit_cost,
        expected_config_adj: selectedPreviewItem.configuration_cost_adjustment,
        new_unit_cost: correctedUnitCost,
        new_config_adj: correctedConfigAdj,
        new_ram: legacyFinalRam.trim() || null,
        new_storage: legacyFinalStorage.trim() || null,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.sales.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.sales.detail(sale.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.reports.all });
      void queryClient.invalidateQueries({ queryKey: ['sales', 'legacy-preview', sale.id] });
      setLegacySaleItemId(null);
      setLegacyReason('');
      setLegacyFinalRam('');
      setLegacyFinalStorage('');
      setLegacyError(null);
      onClose();
    },
    onError: (err: unknown) => {
      setLegacyError(parseDatabaseError(err));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    mutate();
  };

  const startEditPayment = (p: {
    id: string;
    amount: number;
    payment_method: string;
    reference: string | null;
    notes: string | null;
  }) => {
    setEditingPayment(p);
    setPaymentNewAmount(p.amount);
    setPaymentMethod(p.payment_method || 'cash');
    setPaymentReference(p.reference || '');
    setPaymentNotes(p.notes || '');
    setPaymentReason('');
    setPaymentCorrectionError(null);
    setPaymentCorrectionSuccess(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-brand-border">
          <div className="flex items-center space-x-2">
            <Edit3 className="w-5 h-5 text-amber-600" />
            <h2 className="font-bold text-brand-black text-sm">
              Edit / Correct Invoice #{sale.invoice_number}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-brand-muted hover:text-brand-black transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5 text-xs">
            {/* Warning */}
            <div className="flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-900">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
              <div>
                <p className="font-bold">Invoice Correction — Owner / Manager Only</p>
                <p className="mt-0.5 text-amber-800">
                  Corrections are audit-logged. The new invoice total must not be lower than net
                  customer receipts already collected (
                  <span className="font-bold">
                    Rs. {netCustomerReceipts.toLocaleString('en-PK')}
                  </span>
                  ).
                </p>
              </div>
            </div>

            {hasCompletedReturns && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-900 font-semibold">
                Financial values are locked because this invoice has completed returns. Only customer details, warranty, and notes may be modified.
              </div>
            )}

            {/* Customer Section */}
            <div className="border border-brand-border rounded-lg p-3.5 bg-slate-50/50 space-y-2.5">
              <div className="flex items-center justify-between">
                <p className="font-bold text-brand-black uppercase tracking-wider text-[10px] flex items-center space-x-1.5">
                  <User className="w-3.5 h-3.5 text-brand-green" />
                  <span>Customer Assignment</span>
                </p>
                <span className="text-[11px] text-brand-muted">
                  Current: <strong className="text-brand-black">{sale.customer?.name || sale.customer_name_snapshot || 'Walk-in / Cash Customer'}</strong>
                </span>
              </div>

              {isOwner ? (
                <div className="space-y-2 pt-1 border-t border-brand-border/60">
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-1.5 text-xs font-semibold cursor-pointer">
                      <input
                        type="radio"
                        name="customerMode"
                        value="keep"
                        checked={customerMode === 'keep'}
                        onChange={() => setCustomerMode('keep')}
                        className="accent-brand-green"
                      />
                      <span>Keep Current</span>
                    </label>
                    <label className="flex items-center space-x-1.5 text-xs font-semibold cursor-pointer">
                      <input
                        type="radio"
                        name="customerMode"
                        value="walk_in"
                        checked={customerMode === 'walk_in'}
                        onChange={() => setCustomerMode('walk_in')}
                        className="accent-brand-green"
                      />
                      <span className="text-amber-800 flex items-center space-x-1">
                        <UserX className="w-3 h-3" />
                        <span>Change to Walk-in</span>
                      </span>
                    </label>
                    <label className="flex items-center space-x-1.5 text-xs font-semibold cursor-pointer">
                      <input
                        type="radio"
                        name="customerMode"
                        value="change"
                        checked={customerMode === 'change'}
                        onChange={() => setCustomerMode('change')}
                        className="accent-brand-green"
                      />
                      <span className="text-blue-800 flex items-center space-x-1">
                        <User className="w-3 h-3" />
                        <span>Select Customer</span>
                      </span>
                    </label>
                  </div>

                  {customerMode === 'change' && (
                    <div className="space-y-1.5 pt-1.5">
                      <input
                        type="text"
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        placeholder="Search customer by name, phone or email..."
                        className="w-full border border-brand-border rounded px-2.5 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-brand-green"
                      />
                      <select
                        value={selectedCustomerId ?? ''}
                        onChange={(e) => setSelectedCustomerId(e.target.value || null)}
                        className="w-full border border-brand-border rounded px-2.5 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-brand-green"
                      >
                        <option value="">— Select registered customer —</option>
                        {customers.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name} {c.phone ? `(${c.phone})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {customerMode === 'walk_in' && (
                    <p className="text-[11px] text-amber-700 font-medium">
                      ✓ Invoice will be reset to Walk-in / Cash Customer. Linked warranty and laptop owner records will be updated.
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-[11px] text-brand-muted">
                  Customer modification on completed invoices is restricted to Owner.
                </p>
              )}
            </div>

            {/* Reason */}
            <div>
              <label className="block font-bold text-brand-black mb-1">
                Correction Reason <span className="text-brand-red">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder="e.g. Price adjustment agreed with customer, wrong product description, clerical error…"
                className="w-full border border-brand-border rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-brand-green resize-none"
                required
              />
            </div>

            {/* Line Items */}
            {(sale.items ?? []).length > 0 && (
              <div>
                <p className="font-bold text-brand-black mb-2 uppercase tracking-wider text-[10px]">
                  Line Item Corrections {hasCompletedReturns && <span className="text-brand-muted font-normal">(Financials Locked)</span>}
                </p>
                <div className="border border-brand-border rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-brand-bg border-b border-brand-border text-brand-muted font-bold uppercase text-[10px]">
                      <tr>
                        <th className="text-left px-3 py-2">Item</th>
                        <th className="text-center px-3 py-2 w-8">Qty</th>
                        <th className="text-right px-3 py-2 w-24">Unit Price</th>
                        <th className="text-right px-3 py-2 w-24">Line Disc</th>
                        <th className="text-right px-3 py-2 w-24">Line Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-brand-border">
                      {(sale.items ?? []).map((item) => {
                        const corr = itemCorrections[item.id];
                        const lineTotal = corr
                          ? Math.max(0, corr.unit_price * (item.quantity ?? 1) - corr.line_discount)
                          : (item.line_total ?? 0);
                        return (
                          <tr key={item.id} className="hover:bg-brand-bg/50">
                            <td className="px-3 py-2">
                              <p className="font-semibold text-brand-black">{item.item_name}</p>
                              {item.serial_snapshot && (
                                <p className="text-[10px] text-brand-muted font-mono">
                                  S/N: {item.serial_snapshot}
                                </p>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">{item.quantity}</td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                min={0}
                                step={1}
                                disabled={hasCompletedReturns}
                                value={corr?.unit_price ?? item.unit_price}
                                onChange={(e) =>
                                  updateItem(
                                    item.id,
                                    'unit_price',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                className="w-full text-right border border-brand-border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-green disabled:bg-gray-100 disabled:text-brand-muted"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                min={0}
                                step={1}
                                disabled={hasCompletedReturns}
                                value={corr?.line_discount ?? (item.line_discount ?? 0)}
                                onChange={(e) =>
                                  updateItem(
                                    item.id,
                                    'line_discount',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                className="w-full text-right border border-brand-border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-green disabled:bg-gray-100 disabled:text-brand-muted"
                              />
                            </td>
                            <td className="px-3 py-2 text-right font-bold text-brand-black">
                              Rs. {lineTotal.toLocaleString('en-PK')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Overall adjustments */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-bold text-brand-black mb-1">
                  Overall Discount (Rs.)
                </label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  disabled={hasCompletedReturns}
                  value={overallDiscount}
                  onChange={(e) => setOverallDiscount(parseFloat(e.target.value) || 0)}
                  className="w-full border border-brand-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-green disabled:bg-gray-100 disabled:text-brand-muted"
                />
              </div>
              <div>
                <label className="block font-bold text-brand-black mb-1">Tax (Rs.)</label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  disabled={hasCompletedReturns}
                  value={tax}
                  onChange={(e) => setTax(parseFloat(e.target.value) || 0)}
                  className="w-full border border-brand-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-green disabled:bg-gray-100 disabled:text-brand-muted"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block font-bold text-brand-black mb-1">Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full border border-brand-border rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-brand-green resize-none"
              />
            </div>

            {/* Proposed total summary */}
            <div
              className={`p-3 rounded-lg border text-xs ${
                belowReceipts
                  ? 'bg-red-50 border-red-300 text-red-900'
                  : 'bg-green-50 border-green-200 text-green-900'
              }`}
            >
              <div className="flex justify-between font-semibold">
                <span>Proposed Subtotal:</span>
                <span>Rs. {proposedSubtotal.toLocaleString('en-PK')}</span>
              </div>
              <div className="flex justify-between font-bold text-sm mt-1">
                <span>Proposed Grand Total:</span>
                <span>Rs. {proposedTotal.toLocaleString('en-PK')}</span>
              </div>
              {belowReceipts && (
                <p className="mt-1.5 font-semibold text-red-700 text-[11px]">
                  ⚠ Grand total cannot be below net customer receipts (Rs.{' '}
                  {netCustomerReceipts.toLocaleString('en-PK')}). The database will enforce this.
                </p>
              )}

              {belowReceipts && isOwner && !hasCompletedReturns && (
                <div className="mt-2.5 p-3 bg-amber-50 border border-amber-300 rounded-lg text-amber-900 text-xs space-y-2">
                  <div className="flex items-center space-x-1.5 font-bold text-amber-950">
                    <DollarSign className="w-4 h-4 text-amber-600" />
                    <span>Grant Post-Sale Discount & Refund Excess (Rs. {(netCustomerReceipts - proposedTotal).toLocaleString('en-PK')})</span>
                  </div>
                  <p className="text-[11px] text-amber-800">
                    Grand total is reduced below recorded receipts. To grant this discount and return the excess cash to the customer without an item return, record the refund details below:
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-semibold text-amber-900 mb-0.5">Refund Payment Method</label>
                      <select
                        value={discountRefundMethod}
                        onChange={(e) => setDiscountRefundMethod(e.target.value as any)}
                        className="w-full border border-amber-300 rounded px-2 py-1 text-xs bg-white focus:ring-1 focus:ring-amber-500"
                      >
                        <option value="cash">Cash</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="card">Card</option>
                        <option value="mobile_wallet">Mobile Wallet</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-amber-900 mb-0.5">Refund Reference</label>
                      <input
                        type="text"
                        value={discountRefundRef}
                        onChange={(e) => setDiscountRefundRef(e.target.value)}
                        placeholder="e.g. Cash returned / Trx ID"
                        className="w-full border border-amber-300 rounded px-2 py-1 text-xs bg-white focus:ring-1 focus:ring-amber-500"
                      />
                    </div>
                  </div>
                  {discountRefundError && (
                    <p className="text-red-700 font-semibold text-[11px]">{discountRefundError}</p>
                  )}
                  {discountRefundSuccess && (
                    <p className="text-emerald-700 font-semibold text-[11px]">{discountRefundSuccess}</p>
                  )}
                  <button
                    type="button"
                    onClick={() => mutateDiscountRefund()}
                    disabled={isDiscountRefundPending || !reason.trim()}
                    className="w-full mt-1 px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded font-bold text-xs flex items-center justify-center space-x-1.5 shadow-sm disabled:opacity-50"
                  >
                    {isDiscountRefundPending && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}
                    <span>Record Post-Sale Discount Refund (Rs. {(netCustomerReceipts - proposedTotal).toLocaleString('en-PK')})</span>
                  </button>
                </div>
              )}
            </div>

            {submitError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 font-semibold">
                {submitError}
              </div>
            )}

            {/* ── Payments List & Ledger Correction (Owner Only) ── */}
            <div className="border border-blue-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 bg-blue-50 border-b border-blue-200">
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-4 h-4 text-blue-600" />
                  <p className="font-bold text-blue-900 text-[11px] uppercase tracking-wider">
                    Recorded Payments ({payments.length})
                  </p>
                </div>
                <div className="text-[11px] font-semibold text-blue-800 space-x-2">
                  <span>Paid: Rs. {totalPaymentsIn.toLocaleString('en-PK')}</span>
                  {totalPaymentsOut > 0 && (
                    <span className="text-amber-800 font-bold">| Refunded: Rs. {totalPaymentsOut.toLocaleString('en-PK')}</span>
                  )}
                </div>
              </div>

              <div className="p-3 space-y-2.5 text-xs">
                {paymentCorrectionSuccess && (
                  <div className="p-2.5 bg-green-50 border border-green-200 rounded text-green-800 flex items-center space-x-2 font-medium">
                    <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                    <span>{paymentCorrectionSuccess}</span>
                  </div>
                )}

                {payments.length === 0 ? (
                  <p className="text-brand-muted text-[11px]">No payment entries recorded yet.</p>
                ) : (
                  <div className="divide-y divide-brand-border border border-brand-border rounded-md overflow-hidden bg-white">
                    {payments.map((p) => {
                      const isRefund = p.direction === 'out';
                      return (
                        <div
                          key={p.id}
                          className={`p-2.5 flex items-center justify-between hover:bg-slate-50 ${
                            isRefund ? 'bg-amber-50/40 border-l-4 border-l-amber-500' : ''
                          }`}
                        >
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className={`font-bold ${isRefund ? 'text-amber-800 font-bold' : 'text-brand-black'}`}>
                                {isRefund ? '-' : ''}Rs. {(p.amount || 0).toLocaleString('en-PK')}
                              </span>
                              <span
                                className={`px-1.5 py-0.2 text-[10px] font-semibold uppercase rounded ${
                                  isRefund
                                    ? 'bg-amber-100 text-amber-900 border border-amber-300'
                                    : 'bg-slate-100 text-slate-700'
                                }`}
                              >
                                {isRefund ? 'Refund' : (p.payment_method || 'Cash')}
                              </span>
                              {!isRefund && p.payment_method && (
                                <span className="text-brand-muted text-[10px] uppercase">
                                  via {p.payment_method}
                                </span>
                              )}
                              {p.reference && (
                                <span className="text-brand-muted text-[10px] font-mono">
                                  Ref: {p.reference}
                                </span>
                              )}
                            </div>
                            {p.notes && <p className="text-[10px] text-brand-muted mt-0.5">{p.notes}</p>}
                            <p className="text-[9px] text-brand-muted mt-0.5">
                              {new Date(p.created_at).toLocaleDateString('en-PK', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          </div>
                          {isOwner && !isRefund && (
                            <button
                              type="button"
                              onClick={() => startEditPayment(p)}
                              className="px-2.5 py-1 text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                            >
                              Correct Entry
                            </button>
                          )}
                          {isRefund && (
                            <span className="text-[10px] text-amber-700 font-medium italic">
                              Refund (Immutable)
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Sub-Editor for Payment Correction */}
                {editingPayment && (
                  <div className="mt-3 p-3.5 bg-blue-50/70 border border-blue-300 rounded-lg space-y-3">
                    <div className="flex items-start space-x-2 text-blue-900">
                      <HelpCircle className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-xs">Payment Ledger Entry Correction</p>
                        <p className="text-[11px] text-blue-800 mt-0.5 leading-relaxed">
                          <strong>When to use:</strong> Use this ONLY if this payment was entered incorrectly into the ledger (e.g. clerk made a typo).
                          <br />
                          <strong>When NOT to use:</strong> If the customer actually paid this amount and money is being refunded, do NOT edit this payment. Use <em>Process Return / Refund</em> so the cash register and customer balance reflect the physical cash movement.
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2.5">
                      <div>
                        <label className="block font-bold text-brand-black mb-1">
                          Correct Amount (Rs.) <span className="text-brand-red">*</span>
                        </label>
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={paymentNewAmount}
                          onChange={(e) => setPaymentNewAmount(parseFloat(e.target.value) || 0)}
                          className="w-full border border-brand-border rounded px-2.5 py-1.5 bg-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 font-bold"
                          required
                        />
                      </div>
                      <div>
                        <label className="block font-bold text-brand-black mb-1">Payment Method</label>
                        <select
                          value={paymentMethod}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          className="w-full border border-brand-border rounded px-2.5 py-1.5 bg-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="cash">Cash</option>
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="card">Card</option>
                          <option value="cheque">Cheque</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2.5">
                      <div>
                        <label className="block font-bold text-brand-black mb-1">Reference / Trx ID</label>
                        <input
                          type="text"
                          value={paymentReference}
                          onChange={(e) => setPaymentReference(e.target.value)}
                          placeholder="e.g. Bank Ref #12345"
                          className="w-full border border-brand-border rounded px-2.5 py-1.5 bg-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block font-bold text-brand-black mb-1">Notes</label>
                        <input
                          type="text"
                          value={paymentNotes}
                          onChange={(e) => setPaymentNotes(e.target.value)}
                          placeholder="Optional notes"
                          className="w-full border border-brand-border rounded px-2.5 py-1.5 bg-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block font-bold text-brand-black mb-1">
                        Correction Reason <span className="text-brand-red">*</span>
                      </label>
                      <input
                        type="text"
                        value={paymentReason}
                        onChange={(e) => setPaymentReason(e.target.value)}
                        placeholder="e.g. Typo during checkout; customer actually paid Rs. 20,000 cash"
                        className="w-full border border-brand-border rounded px-2.5 py-1.5 bg-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                        required
                      />
                    </div>

                    {paymentCorrectionError && (
                      <div className="p-2 bg-red-50 border border-red-200 rounded text-red-800 text-[11px] font-semibold">
                        {paymentCorrectionError}
                      </div>
                    )}

                    <div className="flex items-center justify-end space-x-2 pt-1">
                      <button
                        type="button"
                        onClick={() => setEditingPayment(null)}
                        className="px-3 py-1 text-xs text-brand-muted hover:text-brand-black"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={isPaymentPending || !paymentReason.trim()}
                        onClick={() => mutatePaymentCorrection()}
                        className="inline-flex items-center space-x-1 px-3 py-1.5 bg-blue-600 text-white text-xs font-bold rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        {isPaymentPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                        <span>Save Payment Correction</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ── Legacy Sold-Laptop Configuration & COGS Correction (Owner Only) ── */}
            {isOwner && eligibleLaptopItems.length > 0 && (
              <div className="border border-purple-200 rounded-lg overflow-hidden">
                <div className="flex items-center space-x-2 px-4 py-2.5 bg-purple-50 border-b border-purple-200">
                  <Wrench className="w-4 h-4 text-purple-600" />
                  <p className="font-bold text-purple-900 text-[11px] uppercase tracking-wider">
                    Legacy Configuration &amp; COGS Correction (Owner Only)
                  </p>
                </div>
                <div className="px-4 py-3 space-y-3 text-xs">
                  <p className="text-brand-muted">
                    Use this to correct the hardware configuration and cost-of-goods for a sold laptop that was configured before this system was adopted. This operation is irreversible and audit-logged.
                  </p>

                  {/* Laptop item selector */}
                  <div>
                    <label className="block font-bold text-brand-black mb-1">
                      Select Laptop Sale Line
                    </label>
                    <select
                      value={legacySaleItemId ?? ''}
                      onChange={(e) => setLegacySaleItemId(e.target.value || null)}
                      className="w-full border border-brand-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">— Select laptop line —</option>
                      {eligibleLaptopItems.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.item_name} {item.serial_snapshot ? `(S/N: ${item.serial_snapshot})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {isLegacyPreviewLoading && legacySaleItemId && (
                    <div className="flex items-center justify-center p-4 text-purple-700 space-x-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Loading legacy diagnostic preview…</span>
                    </div>
                  )}

                  {legacySaleItemId && selectedPreviewItem && (
                    <>
                      {/* Diagnostic Summary Card */}
                      <div className="bg-purple-50/60 border border-purple-200 rounded-lg p-3 space-y-2">
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="font-bold text-purple-950 uppercase tracking-wide">
                            Historical Diagnostic Analysis
                          </span>
                          {selectedPreviewItem.inventory_reconciliation_needed && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 rounded font-semibold text-[10px]">
                              Physical Reconciliation Needed
                            </span>
                          )}
                        </div>

                        {/* Evidence uncertainty banner */}
                        {selectedPreviewItem.evidence_uncertain && (
                          <div className="flex items-start space-x-2 p-2 bg-amber-50 border border-amber-300 rounded text-[11px] text-amber-900">
                            <span className="mt-0.5">⚠️</span>
                            <span>
                              <strong>Uncertain evidence</strong> — source:{' '}
                              <code className="font-mono bg-amber-100 px-1 rounded">
                                {selectedPreviewItem.cost_provenance ?? 'unknown'}
                              </code>
                              . The proposed COGS is an estimate, not a verified historical snapshot. Owner must confirm the values are correct before applying.
                            </span>
                          </div>
                        )}

                        {selectedPreviewItem.cost_provenance === 'missing' && (
                          <div className="flex items-start space-x-2 p-2 bg-red-50 border border-red-300 rounded text-[11px] text-red-900">
                            <span className="mt-0.5">🚫</span>
                            <span>
                              <strong>No cost evidence found</strong> — there is no snapshot, purchase price, or unit cost on record for this item. A zero-cost proposal cannot be applied. Obtain the historical cost before correcting.
                            </span>
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-2 text-[11px] bg-white p-2.5 rounded border border-purple-100">
                          <div>
                            <span className="text-brand-muted">Cost Evidence:</span>{' '}
                            <code className={`font-mono text-[10px] px-1 rounded ${
                              selectedPreviewItem.evidence_uncertain
                                ? 'bg-amber-100 text-amber-900'
                                : 'bg-emerald-50 text-emerald-900'
                            }`}>
                              {selectedPreviewItem.cost_provenance ?? '—'}
                            </code>
                          </div>
                          <div>
                            <span className="text-brand-muted">Proposed Base Cost:</span>{' '}
                            <strong className="text-brand-black">
                              Rs. {(selectedPreviewItem.proposed_base_cost ?? 0).toLocaleString('en-PK')}
                            </strong>
                          </div>
                          <div>
                            <span className="text-brand-muted">Original recorded specs:</span>{' '}
                            <span className="font-mono text-brand-black">
                              {selectedPreviewItem.original_specs?.ram || 'RAM not recorded'} / {selectedPreviewItem.original_specs?.storage || 'Storage not recorded'}
                            </span>
                            <p className="text-[10px] text-brand-muted mt-1">
                              {selectedPreviewItem.original_specs?.source === 'inventory_creation_audit'
                                ? 'From the original inventory creation record.'
                                : selectedPreviewItem.original_specs?.source === 'earliest_inventory_audit'
                                  ? 'Earliest surviving inventory record; verify against the supplier invoice.'
                                  : 'Original evidence is unavailable. Current and sold specs are not proof of the original configuration.'}
                            </p>
                            <p className="text-[10px] text-brand-muted mt-1">
                              Current inventory record: {selectedPreviewItem.laptop_hw_ram || '—'} / {selectedPreviewItem.laptop_hw_storage || '—'}
                            </p>
                          </div>
                          <div>
                            <span className="text-brand-muted">Current Invoice COGS:</span>{' '}
                            <strong className="text-brand-black">
                              Rs. {selectedPreviewItem.unit_cost.toLocaleString('en-PK')}
                            </strong>
                          </div>
                          <div>
                            <span className="text-brand-muted">Sold Snapshot Specs:</span>{' '}
                            <span className="font-mono text-brand-black">
                              {selectedPreviewItem.ram_snapshot || '—'} / {selectedPreviewItem.storage_snapshot || '—'}
                            </span>
                          </div>
                        </div>

                        {/* COGS & Profit Impact Preview */}
                        <div className="grid grid-cols-3 gap-2 text-center pt-1">
                          <div className="p-2 bg-white rounded border border-purple-100">
                            <span className="text-[10px] text-brand-muted block">Current Line Profit</span>
                            <span className={`font-bold text-xs ${selectedPreviewItem.line_profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                              Rs. {selectedPreviewItem.line_profit.toLocaleString('en-PK')}
                            </span>
                          </div>
                          <div className="p-2 bg-white rounded border border-purple-100">
                            <span className="text-[10px] text-purple-800 block font-semibold">Proposed COGS</span>
                            <span className="font-bold text-xs text-purple-900">
                              Rs. {selectedPreviewItem.proposed_cogs.toLocaleString('en-PK')}
                            </span>
                          </div>
                          <div className="p-2 bg-white rounded border border-purple-100">
                            <span className="text-[10px] text-purple-800 block font-semibold">Proposed Profit</span>
                            <span className={`font-bold text-xs ${selectedPreviewItem.proposed_profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                              Rs. {selectedPreviewItem.proposed_profit.toLocaleString('en-PK')}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Hardware Specification Snapshots */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block font-bold text-brand-black mb-1">Final RAM Specification</label>
                          <input
                            type="text"
                            value={legacyFinalRam}
                            onChange={(e) => setLegacyFinalRam(e.target.value)}
                            placeholder="e.g. 16GB DDR4"
                            className="w-full border border-brand-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block font-bold text-brand-black mb-1">Final Storage Specification</label>
                          <input
                            type="text"
                            value={legacyFinalStorage}
                            onChange={(e) => setLegacyFinalStorage(e.target.value)}
                            placeholder="e.g. 512GB NVMe SSD"
                            className="w-full border border-brand-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                      </div>

                      {/* Corrected Financial Values */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block font-bold text-brand-black mb-1">
                            Corrected Total Unit Cost (COGS) <span className="text-brand-red">*</span>
                          </label>
                          <input
                            type="number"
                            min={0}
                            step={1}
                            value={correctedUnitCost}
                            onChange={(e) => setCorrectedUnitCost(parseFloat(e.target.value) || 0)}
                            className="w-full border border-brand-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 font-bold"
                            required
                          />
                          <p className="text-[10px] text-brand-muted mt-0.5">
                            Authoritative unit_cost saved to sale item. Line cost &amp; profit will be recalculated automatically.
                          </p>
                        </div>
                        <div>
                          <label className="block font-bold text-brand-black mb-1">
                            Configuration Cost Adjustment (Rs.)
                          </label>
                          <input
                            type="number"
                            step={1}
                            value={correctedConfigAdj}
                            onChange={(e) => setCorrectedConfigAdj(parseFloat(e.target.value) || 0)}
                            className="w-full border border-brand-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                          <p className="text-[10px] text-brand-muted mt-0.5">
                            Adjustment attributable to RAM/SSD swaps (e.g. +2,000 or -1,500).
                          </p>
                        </div>
                      </div>

                      {/* Projected Profit for Line */}
                      <div className="p-2.5 bg-slate-50 border border-slate-200 rounded flex items-center justify-between text-xs">
                        <span className="text-slate-700">Projected Line Profit with these values:</span>
                        <strong className={`font-bold ${
                          ((selectedPreviewItem.unit_price * selectedPreviewItem.quantity - selectedPreviewItem.line_discount) - (correctedUnitCost * selectedPreviewItem.quantity)) >= 0
                            ? 'text-emerald-700'
                            : 'text-red-700'
                        }`}>
                          Rs. {(((selectedPreviewItem.unit_price * selectedPreviewItem.quantity - selectedPreviewItem.line_discount) - (correctedUnitCost * selectedPreviewItem.quantity))).toLocaleString('en-PK')}
                        </strong>
                      </div>

                      {/* Legacy Reason */}
                      <div>
                        <label className="block font-bold text-brand-black mb-1">
                          Correction Reason <span className="text-brand-red">*</span>
                        </label>
                        <textarea
                          value={legacyReason}
                          onChange={(e) => setLegacyReason(e.target.value)}
                          rows={2}
                          placeholder="e.g. Historical RAM/SSD upgrade cost omitted at sale; correcting accounting COGS to match verified component values."
                          className="w-full border border-brand-border rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                          required
                        />
                      </div>

                      {legacyError && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 font-semibold">
                          {legacyError}
                        </div>
                      )}

                      {/* Uncertain-evidence verification gate */}
                      {selectedPreviewItem.evidence_uncertain && selectedPreviewItem.cost_provenance !== 'missing' && (
                        <label className="flex items-start space-x-2 p-2.5 bg-amber-50 border border-amber-300 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            className="mt-0.5 accent-amber-600"
                            checked={evidenceVerified}
                            onChange={(e) => setEvidenceVerified(e.target.checked)}
                          />
                          <span className="text-[11px] text-amber-900">
                            I, the store owner, have manually verified that the proposed COGS values above are correct despite the uncertain evidence source (
                            <code className="font-mono bg-amber-100 px-0.5 rounded">
                              {selectedPreviewItem.cost_provenance}
                            </code>
                            ). I accept responsibility for this accounting correction.
                          </span>
                        </label>
                      )}

                      <button
                        type="button"
                        onClick={() => {
                          setLegacyError(null);
                          mutateLegacy();
                        }}
                        disabled={
                          isLegacyPending ||
                          !legacyReason.trim() ||
                          correctedUnitCost < 0 ||
                          selectedPreviewItem.cost_provenance === 'missing' ||
                          (selectedPreviewItem.evidence_uncertain && !evidenceVerified)
                        }
                        className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white text-xs font-bold rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLegacyPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                        <span>{isLegacyPending ? 'Applying Legacy Correction…' : 'Apply Legacy COGS Correction'}</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-5 py-4 border-t border-brand-border bg-brand-bg">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black transition-colors"
            >
              ← Return to Invoice
            </button>
            <button
              type="submit"
              disabled={isPending || !reason.trim()}
              className="inline-flex items-center space-x-2 px-5 py-2 bg-amber-600 text-white text-sm font-bold rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              <span>{isPending ? 'Saving Correction…' : 'Save Correction'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
